import React, { useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import localizationService from "../../services/localization-service.js";
import { useSelector, useDispatch } from "react-redux";
import { setSmsData } from "../../actions/portal.action.js";
import VerificationCodeStatus from "../../models/VerificationCodeStatus.js";
import {
  showSmsError,
  parseTimeLeftToSeconds,
  formatSeconds,
} from "../../utils/sms-helpers.js";
import storageService from "../../services/storage-service.js";
import config from "../../config.js";
import MoonLoader from "react-spinners/MoonLoader";
import SmsState from "../../models/SmsState";
import notification from "../../services/notification-service";
import { setSecondsLeft } from "../../actions/portal.action.js";

const SmsVerificationCodeModal = ({
  isOpen,
  onClose,
  phoneNumber,
  isPhoneNumberChanged,
}) => {
  const selectedlang = useSelector((state) => state.account.lang);
  const dispatch = useDispatch();
  const [isDesktopResolution, setIsDesktopResolution] = useState(true);
  const inputSize = isDesktopResolution ? "45px" : "calc((100% - 64px) / 5)";
  const [verificationCode, setVerificationCode] = useState(null);
  const [verificationCodeExpiredError, setVerificationCodeExpiredError] =
    useState(false);
  const [verificationCodeDoNotMatchError, setVerificationCodeDoNotMatchError] =
    useState(false);
  const [maxAttempsExceededError, setMaxAttempsExceededError] = useState(false);
  const [invalidPhoneNumberError, setInvalidPhoneNumberError] = useState(false);
  const [
    maxCodeRequestAttemptsExceededError,
    setMaxCodeRequestAttemptsExceededError,
  ] = useState(false);
  const setSMSDataLoading = useSelector(
    (state) => state.portal.setSMSDataLoading,
  );
  const smsData = useSelector((state) => state.portal.smsData);
  const secondsLeft = useSelector((state) => state.portal.secondsLeft);

  useEffect(() => {
    if (window.innerWidth < 1300) {
      setIsDesktopResolution(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVerificationCode(null);
      setVerificationCodeExpiredError(false);
      setVerificationCodeDoNotMatchError(false);
      setMaxCodeRequestAttemptsExceededError(false);
    } else {
      setMaxAttempsExceededError(false);
      setMaxCodeRequestAttemptsExceededError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (smsData) {
      if (
        smsData.verificationCodeStatus ===
        VerificationCodeStatus.maxAttemptsExceeded
      ) {
        setMaxAttempsExceededError(true);
      } else if (
        smsData.verificationCodeStatus ===
        VerificationCodeStatus.maxCodeRequestAttemptsExceeded
      ) {
        setMaxCodeRequestAttemptsExceededError(true);
      } else {
        setMaxAttempsExceededError(false);
        setMaxCodeRequestAttemptsExceededError(false);
      }
    }
  }, [smsData]);

  useEffect(() => {
    if (!smsData?.blockedDateTimeLeft) {
      dispatch(setSecondsLeft(null));
      return;
    }
  
    const initial = parseTimeLeftToSeconds(smsData.blockedDateTimeLeft);
  
    dispatch(setSecondsLeft(initial));
  
    const interval = setInterval(() => {
      dispatch((dispatch, getState) => {
        const current = getState().portal.secondsLeft;
  
        if (current <= 1) {
          clearInterval(interval);
          dispatch(setSecondsLeft(0));
        } else {
          dispatch(setSecondsLeft(current - 1));
        }
      });
    }, 1000);
  
    return () => clearInterval(interval);
  }, [smsData?.blockedDateTimeLeft]);

  useEffect(() => {
    if (!smsData?.blockedDateTimeLeft) {
      dispatch(setSecondsLeft(null));
      return;
    }
  
    const initial = parseTimeLeftToSeconds(
      smsData.blockedDateTimeLeft
    );
  
    dispatch(setSecondsLeft(initial));
  }, [smsData?.blockedDateTimeLeft]);

  const confirmVerificationCode = () => {
    let request = {
      userId: Number(storageService.getStorageItem(config.USER_ID)),
      cellPhoneNumber: phoneNumber,
      smsState: SmsState.processing,
      code: verificationCode,
    };

    dispatch(setSmsData(request))
      .then(function (response) {
        if (
          response.verificationCodeStatus === VerificationCodeStatus.success
        ) {
          if (isPhoneNumberChanged) {
            notification.success(
              localizationService.getLocalizationValue(
                "cuccessfullyChangedPhoneNumber",
                selectedlang,
              ),
            );
          }
          onClose(true);
        } else if (
          response.verificationCodeStatus === VerificationCodeStatus.expired
        ) {
          setVerificationCodeExpiredError(true);
        } else if (
          response.verificationCodeStatus === VerificationCodeStatus.unsuccess
        ) {
          setVerificationCodeDoNotMatchError(true);
        } else if (
          response.verificationCodeStatus ===
          VerificationCodeStatus.maxAttemptsExceeded
        ) {
          setMaxAttempsExceededError(true);
        } else if (
          response.verificationCodeStatus ===
          VerificationCodeStatus.invalidPhoneNumber
        ) {
          setInvalidPhoneNumberError(true);
        } else if (
          response.verificationCodeStatus ===
          VerificationCodeStatus.maxCodeRequestAttemptsExceeded
        ) {
          setMaxCodeRequestAttemptsExceededError(true);
        }
      })
      .catch(function (res) {
        showSmsError(selectedlang);
      });
  };

  const getNewVerificationCode = () => {
    setVerificationCode(null);
    setVerificationCodeDoNotMatchError(false);

    let request = {
      userId: Number(storageService.getStorageItem(config.USER_ID)),
      cellPhoneNumber: phoneNumber,
      smsState: SmsState.processing,
    };
    dispatch(setSmsData(request))
      .then(function (response) {
        smsData.smsState = SmsState.processing; // if we don't do this, smsData in redux will not be updated and it is important to be updated if user logs out
        setVerificationCodeExpiredError(false);
      })
      .catch(function (res) {
        showSmsError(selectedlang);
      });
  };

  useEffect(() => {
    if (secondsLeft === 0) {
      setVerificationCode(null);
      setMaxAttempsExceededError(false);
      setVerificationCodeDoNotMatchError(false);
      setMaxCodeRequestAttemptsExceededError(false);
    }
  }, [secondsLeft]);

  if (!isOpen) return null;

  return (
    <>
      {setSMSDataLoading ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
          <MoonLoader
            color="#9aa82d"
            size={80}
            loading={setSMSDataLoading}
            aria-label="Loading Spinner"
          />
        </div>
      ) : (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 relative">
              <h3 className="text-lg font-medium text-gray-900">
                {localizationService.getLocalizationValue(
                  verificationCodeExpiredError
                    ? "verificationCodeExpiredTitle"
                    : "verificationCodeModalTitle",
                  selectedlang,
                )}
              </h3>

              <i
                className="fas fa-times absolute right-2 top-2 cursor-pointer text-gray-600 hover:text-black"
                onClick={() => onClose(false)}
              ></i>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              {verificationCodeExpiredError ? (
                <p className="text-sm text-gray-500 mb-3">
                  {localizationService.getLocalizationValue(
                    "verificationCodeExpiredMsg",
                    selectedlang,
                  )}
                </p>
              ) : maxAttempsExceededError && secondsLeft !== 0 ? (
                <>
                  <p className="text-sm text-gray-500 text-center">
                    {localizationService.getLocalizationValue(
                      "maxAttempsExceededErrorMsg",
                      selectedlang,
                    )}
                  </p>
                  <p className="text-sm text-gray-500 text-center">
                    {localizationService.getLocalizationValue(
                      "tryIn30Minutes",
                      selectedlang,
                    )}{" "}
                    {secondsLeft !== null && (
                      <strong>{formatSeconds(secondsLeft)}</strong>
                    )}
                  </p>
                </>
              ) : maxCodeRequestAttemptsExceededError && secondsLeft !== 0 ? (
                <>
                  <p className="text-sm text-gray-500 text-center">
                    {localizationService.getLocalizationValue(
                      "maxCodeRequestAttemptsExceededErrorMsg",
                      selectedlang,
                    )}
                  </p>
                  <p className="text-sm text-gray-500 text-center">
                    {localizationService.getLocalizationValue(
                      "tryIn30Minutes",
                      selectedlang,
                    )}{" "}
                    {secondsLeft !== null && (
                      <strong>{formatSeconds(secondsLeft)}</strong>
                    )}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-3">
                    {localizationService.getLocalizationValue(
                      "verificationCodeModalPartOne",
                      selectedlang,
                    )}
                  </p>
                  <OtpInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    numInputs={5}
                    containerStyle="flex justify-center w-full gap-4"
                    inputStyle={`p-0 text-center border rounded-md !leading-none box-border ${
                      isDesktopResolution ? "w-[45px] h-[45px]" : ""
                    }`}
                    renderInput={(props) => (
                      <input
                        {...props}
                        style={{
                          ...props.style,
                          type: "number",
                          width: inputSize,
                          height: isDesktopResolution ? "45px" : "auto",
                          aspectRatio: "1",
                          boxSizing: "border-box",
                        }}
                      />
                    )}
                  />

                  <div className="flex flex-col justify-center items-center mt-4 gap-1">
                    {verificationCodeDoNotMatchError ? (
                      <>
                        <p className="text-sm text-center text-red-500">
                          {localizationService.getLocalizationValue(
                            "verificationCodeDoesNotMatchErrorP1",
                            selectedlang,
                          )}
                        </p>
                        <p className="text-sm text-center text-red-500 mb-1">
                          {localizationService.getLocalizationValue(
                            "verificationCodeDoesNotMatchErrorP2",
                            selectedlang,
                          )}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-500 text-center">
                          {localizationService.getLocalizationValue(
                            "didntReceiveCode",
                            selectedlang,
                          )}
                        </p>
                        <p className="text-sm text-gray-500 text-center">
                          {localizationService.getLocalizationValue(
                            "checkYourPhoneMsg",
                            selectedlang,
                          )}{" "}
                          <a
                            className="font-bold text-[#9AA82D] hover:text-[#7d8a1f]"
                            onClick={() => getNewVerificationCode()}
                          >
                            {localizationService.getLocalizationValue(
                              "requestNewCode",
                              selectedlang,
                            )}
                          </a>
                          .
                        </p>
                      </>
                    )}
                    {verificationCodeDoNotMatchError && (
                      <button
                        onClick={() => getNewVerificationCode()}
                        className={`inline-flex items-center ${!isDesktopResolution ? "w-[60%] justify-center" : ""} 
                          px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 
                          focus:ring-offset-2 focus:ring-primary`}
                      >
                        {localizationService.getLocalizationValue(
                          "getNewVerificationCode",
                          selectedlang,
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-center space-x-3">
              <button
                onClick={() =>
                  verificationCodeExpiredError
                    ? getNewVerificationCode()
                    : confirmVerificationCode()
                }
                className={`inline-flex items-center justify-center ${!isDesktopResolution ? "w-[100%]" : "w-[70%]"} 
              ${(verificationCode?.length !== 5 && !verificationCodeExpiredError) || (maxAttempsExceededError && secondsLeft !== 0) || (maxCodeRequestAttemptsExceededError && secondsLeft !== 0) ? "bg-gray-300 text-gray-500 hover:bg-gray-300 cursor-not-allowed" : ""} 
              px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 
              focus:ring-offset-2 focus:ring-primary`}
                disabled={
                  (verificationCode?.length !== 5 &&
                    !verificationCodeExpiredError) ||
                  (maxAttempsExceededError && secondsLeft !== 0) ||
                  (maxCodeRequestAttemptsExceededError && secondsLeft !== 0)
                }
              >
                {localizationService.getLocalizationValue(
                  verificationCodeExpiredError
                    ? "getNewVerificationCode"
                    : "confirm",
                  selectedlang,
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SmsVerificationCodeModal;
